import { Shield, Layers, Wrench } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const PILLAR_ICONS = [Shield, Layers, Wrench];
const ABOUT_IMAGE =
  "https://images.pexels.com/photos/16282743/pexels-photo-16282743.jpeg";

export const About = () => {
  const { t } = useLang();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-16 lg:py-20 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <SectionEyebrow>{t.about.eyebrow}</SectionEyebrow>
            <h2
              data-testid="about-title"
              className="mt-5 font-heading font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight max-w-2xl"
            >
              {t.about.title}
            </h2>

            <div className="mt-8 space-y-5 text-slate-300 text-base leading-relaxed max-w-2xl">
              {t.about.paragraphs.map((p, i) => (
                <p key={i} data-testid={`about-paragraph-${i}`}>
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-12 grid sm:grid-cols-3 gap-4">
              {t.about.pillars.map((pillar, i) => {
                const Icon = PILLAR_ICONS[i];
                return (
                  <div
                    key={i}
                    data-testid={`about-pillar-${i}`}
                    className="border border-white/10 bg-white/[0.02] p-5 rounded-sm hover:border-[#00E5FF]/30 transition-colors duration-300"
                  >
                    <Icon
                      size={20}
                      strokeWidth={1.5}
                      className="text-[#00E5FF]"
                    />
                    <h3 className="mt-4 font-heading font-bold text-white text-base">
                      {pillar.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                      {pillar.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
              <div className="absolute inset-0 border border-white/10 rounded-sm overflow-hidden">
                <img
                  src={ABOUT_IMAGE}
                  alt="Astana skyline"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/40 to-transparent" />
              </div>
              <div className="absolute -top-4 -right-4 hidden md:block border border-[#00E5FF]/40 bg-[#0B0F19]/90 backdrop-blur-md px-4 py-3 rounded-sm">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E5FF]">
                  HQ
                </div>
                <div className="mt-1 text-sm text-white font-heading font-bold">
                  Astana · KZ
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 hidden md:flex items-center gap-2 border border-white/10 bg-[#0B0F19]/90 backdrop-blur-md px-4 py-3 rounded-sm">
                <span className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-300">
                  Program active
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

function SectionEyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-2 text-[#00E5FF] font-mono text-xs uppercase tracking-[0.24em]">
      <span className="w-6 h-px bg-[#00E5FF]" />
      {children}
    </div>
  );
}

export default About;
